"use client";

import Link from "next/link";
import { useId, useRef, useState } from "react";
import { PackageSearch, Store } from "lucide-react";
import YourBarrioLoader from "@/components/ui/YourBarrioLoader";
import { normalizeInventory } from "@/lib/inventory";

const TABS = ["items", "shops"];

export function SearchResultThumbnail({ src, fallback, testId }) {
  const [failedSrc, setFailedSrc] = useState(null);
  const normalizedSrc = typeof src === "string" ? src.trim() : "";
  const showImage = Boolean(normalizedSrc) && failedSrc !== normalizedSrc;

  if (!showImage) return fallback;

  return (
    <img
      src={normalizedSrc}
      alt=""
      data-testid={testId}
      referrerPolicy={/^https?:\/\//i.test(normalizedSrc) ? "no-referrer" : undefined}
      className="h-10 w-10 shrink-0 rounded-lg border border-white/10 object-cover"
      onError={() => setFailedSrc(normalizedSrc)}
    />
  );
}

export function SearchResultAvailability({ item, testId }) {
  const inventory = normalizeInventory(item);

  if (inventory.availability === "available" || inventory.availability === "low") {
    return null;
  }

  const label =
    inventory.availability === "out" &&
    (inventory.label === "Out of stock" || inventory.label === "Sold")
      ? "Sold out"
      : "Unavailable";

  return (
    <div data-testid={testId} className="mt-0.5 text-[11px] yb-dropdown-muted">
      {label}
    </div>
  );
}

export default function MobileSearchResultsTabs({
  open,
  query,
  items = [],
  businesses = [],
  loading = false,
  error = null,
  onItemSelect = undefined,
  onClose = undefined,
}) {
  const [activeTab, setActiveTab] = useState("items");
  const id = useId();
  const tabRefs = useRef({});

  if (!open) return null;

  const selectTab = (nextTab, { focus = false } = {}) => {
    setActiveTab(nextTab);
    if (focus) {
      requestAnimationFrame(() => tabRefs.current[nextTab]?.focus());
    }
  };

  const handleTabKeyDown = (event, currentTab) => {
    const currentIndex = TABS.indexOf(currentTab);
    let nextIndex = currentIndex;

    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % TABS.length;
    else if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = TABS.length - 1;
    else return;

    event.preventDefault();
    selectTab(TABS[nextIndex], { focus: true });
  };

  const tabClassName = (tab) =>
    `yb-mobile-search-tab flex min-h-11 flex-1 items-center justify-center gap-2 border-b-2 px-3 py-2 text-sm font-semibold transition focus-visible:outline-none ${
      activeTab === tab
        ? "border-purple-400"
        : "border-transparent yb-dropdown-muted hover:border-white/20 hover:text-white"
    }`;

  const itemTabId = `${id}-items-tab`;
  const shopTabId = `${id}-shops-tab`;
  const itemPanelId = `${id}-items-panel`;
  const shopPanelId = `${id}-shops-panel`;

  return (
    <div
      className="fixed left-3 right-3 top-20 z-50 mt-2 max-h-[calc(100dvh-6rem)] overflow-y-auto lg:hidden"
      data-testid="mobile-search-results"
    >
      <div className="rounded-2xl p-3 yb-dropdown-surface">
        {error ? (
          <div className="mb-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-rose-200">
            {error}
          </div>
        ) : null}
        <div
          className="mb-3 flex w-full border-b border-white/10"
          role="tablist"
          aria-label="Search result types"
        >
          <button
            ref={(node) => {
              tabRefs.current.items = node;
            }}
            id={itemTabId}
            type="button"
            role="tab"
            aria-selected={activeTab === "items"}
            aria-controls={itemPanelId}
            tabIndex={activeTab === "items" ? 0 : -1}
            className={tabClassName("items")}
            onClick={() => selectTab("items")}
            onKeyDown={(event) => handleTabKeyDown(event, "items")}
          >
            <PackageSearch className="h-4 w-4" aria-hidden="true" />
            Items
            {loading ? <YourBarrioLoader size="xs" className="yb-dropdown-muted" /> : null}
          </button>
          <button
            ref={(node) => {
              tabRefs.current.shops = node;
            }}
            id={shopTabId}
            type="button"
            role="tab"
            aria-selected={activeTab === "shops"}
            aria-controls={shopPanelId}
            tabIndex={activeTab === "shops" ? 0 : -1}
            className={tabClassName("shops")}
            onClick={() => selectTab("shops")}
            onKeyDown={(event) => handleTabKeyDown(event, "shops")}
          >
            <Store className="h-4 w-4" aria-hidden="true" />
            Shops
          </button>
        </div>

        {activeTab === "items" ? (
          <div
            id={itemPanelId}
            role="tabpanel"
            aria-labelledby={itemTabId}
            className="space-y-2"
          >
            {items.map((item) => (
              <button
                key={`mobile-item-${item.id}`}
                type="button"
                onClick={() => onItemSelect?.(item)}
                className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left transition flex items-start gap-3 yb-dropdown-item"
              >
                <SearchResultThumbnail
                  src={item.photo_url}
                  testId="mobile-listing-image"
                  fallback={
                    <div
                      aria-hidden="true"
                      data-testid="mobile-listing-fallback"
                      className="h-10 w-10 shrink-0 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center text-[11px] font-semibold text-white/80"
                    >
                      {item.category ? item.category.slice(0, 3).toUpperCase() : "AI"}
                    </div>
                  }
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold leading-snug">{item.title}</div>
                  <div className="text-[11px] yb-dropdown-muted">
                    {item.category || "Local listing"}
                    {item.price ? ` · $${item.price}` : ""}
                  </div>
                  <SearchResultAvailability
                    item={item}
                    testId="mobile-item-availability"
                  />
                </div>
              </button>
            ))}
            {!loading && !error && items.length === 0 ? (
              <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-4 text-sm yb-dropdown-muted">
                No items found for “{query.trim()}”.
              </div>
            ) : null}
          </div>
        ) : (
          <div
            id={shopPanelId}
            role="tabpanel"
            aria-labelledby={shopTabId}
            className="space-y-2"
          >
            {businesses.map((business) => (
              <Link
                key={`mobile-shop-${business.id}`}
                href={`/b/${encodeURIComponent(business.id)}`}
              onClick={onClose}
              className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left transition flex items-start gap-3 yb-dropdown-item cursor-pointer"
            >
                <SearchResultThumbnail
                  src={business.image}
                  testId="mobile-shop-image"
                  fallback={
                    <div
                      aria-hidden="true"
                      data-testid="mobile-shop-fallback"
                      className="h-10 w-10 shrink-0 rounded-lg bg-emerald-500/20 border border-emerald-200/30 flex items-center justify-center"
                    >
                      <Store className="h-4 w-4 text-emerald-200" aria-hidden="true" />
                    </div>
                  }
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold leading-snug">{business.name}</div>
                  <div className="text-[11px] yb-dropdown-muted">
                    {business.category || "Local business"}
                    {business.city ? ` · ${business.city}` : ""}
                  </div>
                </div>
              </Link>
            ))}
            {!loading && !error && businesses.length === 0 ? (
              <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-4 text-sm yb-dropdown-muted">
                No shops found for “{query.trim()}”.
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
